// Third Party
import { toInteger } from 'lodash';
import React from 'react';

// Utils
import { QuoteItemizedType } from '@gql';
import { formatWholeDollarAmount } from '@utils/money';

// UI
import { Typography } from '@components/typography';
import { CustomTooltip, useCustomTooltip } from '@components/custom-tooltip';
import { Icon, IconNames } from '@components/icon';

export type IQuoteBreakdownProps = {
  numberOfNights: number;
  totalWithoutTaxes: number;
  quoteBreakdownItems: {
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  }[];
};

export const ListingQuoteBreakdown: React.FC<IQuoteBreakdownProps> = ({
  numberOfNights,
  totalWithoutTaxes,
  quoteBreakdownItems,
}) => {
  const { toggle: toggleServiceFeeInfo1, isVisible: serviceFeeInfo1Visible } =
    useCustomTooltip();
  const { toggle: toggleHostFeeInfo, isVisible: hostFeeInfoVisible } =
    useCustomTooltip();
  const serviceFeeInfoRef1 = React.useRef<HTMLElement>(null);
  const hostFeeInfoRef = React.useRef<HTMLElement>(null);

  // Function to get the order of the types
  const getOrder = (type: QuoteItemizedType) => {
    switch (type) {
      case QuoteItemizedType.Base:
        return 1;
      case QuoteItemizedType.Fee:
        return 2;
      case QuoteItemizedType.Other:
        return 3;
      case QuoteItemizedType.Discount:
        return 4;
      default:
        return 5;
    }
  };

  // Sorting the items based on the type order
  const quoteItems = [...quoteBreakdownItems].sort((a, b) => {
    return getOrder(a.type) - getOrder(b.type);
  });

  const categorizedQuoteItems = quoteItems.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    },
    {} as Record<QuoteItemizedType, typeof quoteItems>
  );

  const feeCents = categorizedQuoteItems[
    QuoteItemizedType.Fee || QuoteItemizedType.Other
  ]?.reduce((acc, item) => acc + item.costCents, 0);

  return (
    <div className="relative space-y-2">
      {quoteItems?.length === 0 && (
        <div className="flex w-full justify-between space-x-2">
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              Math.round(totalWithoutTaxes / numberOfNights)
            )}{' '}
            x {numberOfNights} nights
          </Typography>

          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(totalWithoutTaxes)}
          </Typography>
        </div>
      )}

      {categorizedQuoteItems[QuoteItemizedType.Base]?.length > 0 && (
        <div className="flex w-full justify-between space-x-2">
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              Math.round(
                categorizedQuoteItems[QuoteItemizedType.Base][0].costCents /
                  numberOfNights
              )
            )}{' '}
            x {numberOfNights} nights
          </Typography>
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              categorizedQuoteItems[QuoteItemizedType.Base][0].costCents
            )}
          </Typography>
        </div>
      )}

      {feeCents > 0 && (
        <div className="flex w-full justify-between space-x-1">
          <div
            className="hover:cursor-pointer hover:opacity-80 flex flex-row space-x-1 items-center"
            ref={hostFeeInfoRef as React.RefObject<HTMLDivElement>}
            onClick={toggleHostFeeInfo}
          >
            <Typography
              component="span"
              variant="body2"
              className="font-medium underline"
            >
              Host charges
            </Typography>
            <Icon name={IconNames.INFO_CIRCLE} className="w-3 h-3" />
          </div>
          <CustomTooltip
            id="tooltip"
            onClickAway={() => hostFeeInfoVisible && toggleHostFeeInfo()}
            setIsOpen={toggleHostFeeInfo}
            isOpen={hostFeeInfoVisible}
            reference={hostFeeInfoRef.current}
            placement="bottom-start"
          >
            <span className="text-black font-medium text-left">
              Charged by your host to cover cleaning and other stay-related
              costs. These fees go entirely to the host and are the same across
              other vacation rental platforms.
            </span>
          </CustomTooltip>

          <div className="flex flex-row space-x-1.5 justify-end pl-2">
            <Typography
              component="span"
              variant="body2"
              className="font-medium"
            >
              {formatWholeDollarAmount(toInteger(feeCents))}
            </Typography>
          </div>
        </div>
      )}

      {categorizedQuoteItems[QuoteItemizedType.Discount]?.map(
        (quoteItem, index) => (
          <div key={index}>
            <PrimaryBreakdown quoteBreakdownItem={quoteItem} />
          </div>
        )
      )}

      <div className="flex w-full justify-between space-x-1 border-b border-primary-dark pb-4">
        <div
          className="flex flex-row space-x-1 items-center"
          ref={serviceFeeInfoRef1 as React.RefObject<HTMLDivElement>}
          onClick={toggleServiceFeeInfo1}
        >
          <Typography
            component="span"
            variant="body2"
            className="font-medium flex-nowrap underline"
          >
            Savvy service fee
          </Typography>
          <Icon name={IconNames.INFO_CIRCLE} className="w-3 h-3" />
        </div>
        <CustomTooltip
          id="tooltip"
          onClickAway={() => serviceFeeInfo1Visible && toggleServiceFeeInfo1()}
          setIsOpen={toggleServiceFeeInfo1}
          isOpen={serviceFeeInfo1Visible}
          reference={serviceFeeInfoRef1.current}
          placement="bottom-start"
        >
          <span className="text-black font-medium text-left">
            Savvy does not charge service fees to the traveler or the host.
          </span>
        </CustomTooltip>

        <div className="flex flex-row space-x-1.5 justify-end pl-2">
          <Typography variant="body2" className="font-medium">
            $0
          </Typography>
        </div>
      </div>

      <div className="flex w-full justify-between space-x-2 pt-2">
        <Typography component="span" variant="subtitle1" className="font-bold">
          Total before taxes
        </Typography>

        <Typography component="span" variant="subtitle1" className="font-bold">
          {formatWholeDollarAmount(totalWithoutTaxes)}
        </Typography>
      </div>
    </div>
  );
};

export type QuoteBreakdownItemProps = {
  quoteBreakdownItem: {
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  };
};

export const PrimaryBreakdown: React.FC<QuoteBreakdownItemProps> = ({
  quoteBreakdownItem,
}) => {
  return (
    <div className="flex w-full justify-between space-x-2">
      <Typography component="span" variant="subtitle2">
        {quoteBreakdownItem.description}
      </Typography>
      <Typography component="span" variant="body2" className="font-medium">
        {formatWholeDollarAmount(quoteBreakdownItem.costCents)}
      </Typography>
    </div>
  );
};
